"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ChatThread, ChatUIMessage } from "@/schema"
import { useChat } from "@ai-sdk/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { cn } from "@ui/utils"
import type { FileUIPart } from "ai"
import { toast } from "sonner"

import { resolveApiUrl, type ApiContext } from "@/hooks/api/base"
import { fetchChatThread } from "@/hooks/api/chat-threads/api"
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation"
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message"
import {
  PromptInput,
  PromptInputAttachmentButton,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input"
import { ActionLinkPart } from "@/components/chat-threads/action-link-part"
import {
  ChatAttachmentTiles,
  ChatPendingAttachments,
  type ChatAttachment,
} from "@/components/chat-threads/chat-attachments"
import { ChatLoadingIndicator } from "@/components/chat-threads/chat-loading-indicator"
import { ChatToolActivityDisplay, collectChatToolActivities } from "@/components/chat-threads/chat-tool-activity"
import { TwoStepChatTransport } from "@/components/chat-threads/two-step-chat-transport"
import { VoiceMicButton } from "@/components/voice-transcription/voice-mic-button"

const MAX_ATTACHMENT_FILE_SIZE = 20 * 1024 * 1024
const ACCEPTED_ATTACHMENT_TYPES = ".pdf,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg,.gif"

const SEND_ERROR_FALLBACK = "Something went wrong sending your message. Please try again."

export interface ArtifactChatPanelProps {
  threadId: string
  getContext: () => Promise<ApiContext | null>
  emptyStateTitle: string
  emptyStateDescription: string
  placeholder: string
  updateToolNames: string[]
  requestContext?: Record<string, unknown> | (() => Record<string, unknown>)
  beforeSubmit?: () => boolean | Promise<boolean>
  onArtifactUpdated?: () => void | Promise<void>
  onBusyChange?: (busy: boolean) => void
  onArtifactUpdatingChange?: (updating: boolean) => void
  autoFocus?: boolean
  className?: string
}

// The chat transport throws `new Error(<Rails response body>)`, so surface the
// `{ errors: [...] }` / `{ error: "..." }` JSON when present.
function sendErrorMessage(error: unknown): string {
  if (!(error instanceof Error) || !error.message) return SEND_ERROR_FALLBACK

  try {
    const parsed = JSON.parse(error.message) as { errors?: unknown; error?: unknown }
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) return parsed.errors.join(". ")
    if (typeof parsed.error === "string" && parsed.error) return parsed.error
  } catch {
    // Not a JSON error body — fall back to the raw message.
  }

  return error.message || SEND_ERROR_FALLBACK
}

export function useArtifactChatThread(threadId: string | undefined, getContext: () => Promise<ApiContext | null>) {
  return useQuery<ChatThread, Error>({
    queryKey: ["chat_thread", threadId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchChatThread(context, threadId as string)
    },
    enabled: !!threadId,
  })
}

export function ArtifactChatPanel({
  threadId,
  thread,
  getContext,
  emptyStateTitle,
  emptyStateDescription,
  placeholder,
  updateToolNames,
  requestContext,
  beforeSubmit,
  onArtifactUpdated,
  onBusyChange,
  onArtifactUpdatingChange,
  autoFocus,
  className,
}: ArtifactChatPanelProps & { thread: ChatThread }) {
  const queryClient = useQueryClient()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingUploadFilesRef = useRef<File[]>([])
  const [stopRequested, setStopRequested] = useState(false)
  const [preparingSubmit, setPreparingSubmit] = useState(false)
  const [refreshingArtifact, setRefreshingArtifact] = useState(false)
  const [refreshingAfterUpdate, setRefreshingAfterUpdate] = useState(false)
  const artifactRefreshCountRef = useRef(0)
  const updateRefreshCountRef = useRef(0)

  const refreshArtifact = useCallback(async () => {
    if (!onArtifactUpdated) return

    artifactRefreshCountRef.current += 1
    setRefreshingArtifact(true)
    try {
      await onArtifactUpdated()
    } finally {
      artifactRefreshCountRef.current -= 1
      if (artifactRefreshCountRef.current === 0) setRefreshingArtifact(false)
    }
  }, [onArtifactUpdated])

  const refreshAfterUpdate = useCallback(async () => {
    updateRefreshCountRef.current += 1
    setRefreshingAfterUpdate(true)
    try {
      await refreshArtifact()
    } finally {
      updateRefreshCountRef.current -= 1
      if (updateRefreshCountRef.current === 0) setRefreshingAfterUpdate(false)
    }
  }, [refreshArtifact])

  const transport = useMemo(
    () =>
      new TwoStepChatTransport<ChatUIMessage>({
        threadId,
        getContext: async () => {
          const context = await getContext()
          return { token: context?.token ?? undefined, bookId: context?.bookId ?? undefined }
        },
        buildBody: ({ messages, body, trigger, messageId }) => {
          const targetMessage = (messages as ChatUIMessage[]).at(-1)
          const resolvedRequestContext = typeof requestContext === "function" ? requestContext() : requestContext
          return {
            ...body,
            ...resolvedRequestContext,
            trigger,
            message_id: messageId,
            message: targetMessage,
            user_prompt: textFromMessage(targetMessage),
          }
        },
        buildFetchInit: (init, context) => {
          const headers = new Headers(init?.headers)
          if (context.token) {
            headers.set("Authorization", `Bearer ${context.token}`)
          }

          const uploadedFiles = pendingUploadFilesRef.current
          if (uploadedFiles.length > 0) {
            headers.delete("Content-Type")
            return { ...init, body: buildUploadFormData(init?.body, uploadedFiles), headers }
          }

          return { ...init, headers }
        },
      }),
    [getContext, requestContext, threadId]
  )

  const { messages, sendMessage, setMessages, status, stop } = useChat<ChatUIMessage>({
    id: threadId,
    messages: thread.ui_messages ?? [],
    resume: !!thread.active_stream_id,
    transport,
    experimental_throttle: 50,
    onError: (error) => {
      toast.error(sendErrorMessage(error))
    },
    onFinish: () => {
      queryClient.invalidateQueries({ queryKey: ["chat_thread", threadId] })
      void refreshArtifact().catch(() => undefined)
    },
  })

  const effectiveStatus = stopRequested ? "ready" : preparingSubmit ? "submitted" : status
  const responding = status === "submitted" || status === "streaming"
  const busy = preparingSubmit || responding || stopRequested || refreshingArtifact

  useEffect(() => {
    onBusyChange?.(busy)
  }, [busy, onBusyChange])

  useEffect(() => () => onBusyChange?.(false), [onBusyChange])

  const lastMessage = messages.at(-1)
  const assistantActivityStarted =
    lastMessage?.role === "assistant" &&
    lastMessage.parts.some(
      (part) =>
        (part.type === "text" && part.text.trim().length > 0) ||
        part.type === "data-progress-update" ||
        part.type === "data-tool-call" ||
        part.type === "data-automation-action" ||
        part.type === "data-status"
    )
  const showThinking = responding && !stopRequested && !assistantActivityStarted

  // Refresh the preview as soon as each update tool lands instead of waiting
  // for the turn to finish.
  const completedUpdateCount =
    lastMessage?.role === "assistant"
      ? lastMessage.parts.filter(
          (part) =>
            part.type === "data-tool-call" && updateToolNames.includes(part.data.name) && part.data.finished_message
        ).length
      : 0
  const updateToolInProgress =
    lastMessage?.role === "assistant" &&
    lastMessage.parts.some(
      (part) =>
        part.type === "data-tool-call" && updateToolNames.includes(part.data.name) && !part.data.finished_message
    )
  const artifactUpdating = updateToolInProgress || refreshingAfterUpdate

  useEffect(() => {
    onArtifactUpdatingChange?.(artifactUpdating)
  }, [artifactUpdating, onArtifactUpdatingChange])

  useEffect(() => () => onArtifactUpdatingChange?.(false), [onArtifactUpdatingChange])

  const seenUpdateCountRef = useRef(0)
  useEffect(() => {
    if (completedUpdateCount > seenUpdateCountRef.current) {
      void refreshAfterUpdate().catch(() => undefined)
    }
    seenUpdateCountRef.current = completedUpdateCount
  }, [completedUpdateCount, refreshAfterUpdate])

  const threadSyncKey = `${thread.id}:${String(thread.updated_at)}:${(thread.ui_messages ?? [])
    .map((message) => message.id)
    .join(",")}`
  const syncedThreadMessagesKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (status !== "ready") return
    if (syncedThreadMessagesKeyRef.current === threadSyncKey) return

    syncedThreadMessagesKeyRef.current = threadSyncKey
    setMessages(thread.ui_messages ?? [])
  }, [setMessages, status, thread, threadSyncKey])

  const stopStreaming = useCallback(() => {
    const lastUserMessage = previousUserMessage(messages)

    setStopRequested(true)
    setMessages(removeInterruptedTurn(messages))
    if (textareaRef.current) {
      if (!textareaRef.current.value) textareaRef.current.value = textFromMessage(lastUserMessage)
      textareaRef.current.focus()
    }
    stop()
    void getContext()
      .then((context) => {
        const url = resolveApiUrl(`chat_threads/${threadId}/chat_stream_cancellation`)
        if (context?.bookId) url.searchParams.set("book_id", context.bookId)
        const headers = new Headers()
        if (context?.token) headers.set("Authorization", `Bearer ${context.token}`)
        return fetch(url.toString(), { method: "POST", headers })
      })
      .catch(() => undefined)
      .finally(() => setStopRequested(false))
  }, [getContext, messages, setMessages, stop, threadId])

  const prepareSubmit = async () => {
    if (busy) {
      textareaRef.current?.focus()
      return false
    }

    setPreparingSubmit(true)
    try {
      if (!beforeSubmit || (await beforeSubmit())) return true

      setPreparingSubmit(false)
      textareaRef.current?.focus()
      return false
    } catch (error) {
      setPreparingSubmit(false)
      throw error
    }
  }

  const submit = async ({ text, files }: PromptInputMessage) => {
    const query = text.trim()
    if (!query && files.length === 0) {
      setPreparingSubmit(false)
      return
    }

    try {
      setStopRequested(false)
      const submittedAt = new Date().toISOString()
      const fileParts: FileUIPart[] = files.map(({ file, ...part }) => {
        void file
        return part
      })
      pendingUploadFilesRef.current = files.length ? await Promise.all(files.map(uploadFileFromPromptPart)) : []

      await sendMessage(
        fileParts.length
          ? {
              metadata: { created_at: submittedAt },
              role: "user",
              parts: [...(query ? [{ type: "text" as const, text: query }] : []), ...fileParts],
            }
          : { text: query, metadata: { created_at: submittedAt } }
      )
    } finally {
      pendingUploadFilesRef.current = []
      setPreparingSubmit(false)
    }
  }

  return (
    <div className={cn("flex h-full min-h-0 flex-col overflow-hidden rounded-md border", className)}>
      <Conversation className="min-h-0 flex-1">
        <ConversationContent className="gap-4 p-4">
          {messages.length === 0 && (
            <ConversationEmptyState
              className="min-h-64 px-6"
              title={emptyStateTitle}
              description={emptyStateDescription}
            />
          )}
          {messages.filter(hasVisibleContent).map((message) => (
            <ArtifactMessage
              key={message.id}
              message={message}
              isAnimating={responding && message.id === lastMessage?.id}
            />
          ))}
          <ChatLoadingIndicator visible={showThinking} className="-mt-3 -ml-1.5 self-start" />
        </ConversationContent>
        <ConversationScrollButton className="bottom-2 size-8" />
      </Conversation>

      <div className="shrink-0 p-3 pt-2">
        <PromptInput
          accept={ACCEPTED_ATTACHMENT_TYPES}
          multiple
          maxFileSize={MAX_ATTACHMENT_FILE_SIZE}
          onError={(error) => toast.error(error.message)}
          beforeSubmit={prepareSubmit}
          onSubmit={submit}
          inputGroupClassName="min-h-20"
        >
          <ChatPendingAttachments compact />
          <PromptInputTextarea
            ref={textareaRef}
            autoFocus={autoFocus}
            placeholder={placeholder}
            className="placeholder:text-muted-foreground min-h-10 px-3 pt-3 pb-0 text-sm md:text-sm"
          />
          <PromptInputFooter className="justify-end gap-2 px-3 pb-2.5">
            <PromptInputTools className="gap-1">
              <PromptInputAttachmentButton />
              <VoiceMicButton editorRef={textareaRef} localActive size="iconXs" className="rounded-full" />
            </PromptInputTools>
            <PromptInputSubmit
              aria-label={busy ? "Stop updating" : "Submit"}
              status={effectiveStatus}
              onStop={stopStreaming}
              size="icon-sm"
              className="bg-brand hover:bg-brand-hover size-8 rounded-full text-black"
            />
          </PromptInputFooter>
        </PromptInput>
      </div>
    </div>
  )
}

function ArtifactMessage({ message, isAnimating }: { message: ChatUIMessage; isAnimating: boolean }) {
  const text = textFromMessage(message)
  const attachments = attachmentsFromParts(message.parts)
  const progressUpdate = message.parts.findLast((part) => part.type === "data-progress-update")
  const progressMessage = progressUpdate?.data.message ?? undefined
  const showProgressUpdate = message.role === "assistant" && progressUpdate?.data.visible !== false && !!progressMessage
  const { activities, associatedStatusIndexes } = collectChatToolActivities(message)
  const standaloneStatuses = message.parts.flatMap((part, index) =>
    part.type === "data-status" && !associatedStatusIndexes.has(index) && part.data.message
      ? [{ id: part.id ?? `status-${index}`, message: part.data.message }]
      : []
  )

  return (
    <Message from={message.role} className="max-w-full">
      <ChatAttachmentTiles
        attachments={attachments}
        compact
        className={message.role === "user" ? "ml-auto justify-end" : "ml-0 justify-start"}
      />
      {showProgressUpdate && (
        <MessageContent>
          <MessageResponse>{progressMessage}</MessageResponse>
        </MessageContent>
      )}
      <ChatToolActivityDisplay activities={activities} message={message} isAnimating={isAnimating} />
      {!showProgressUpdate &&
        standaloneStatuses.map((status) => (
          <div key={status.id} className="text-muted-foreground text-sm">
            {status.message}
          </div>
        ))}
      {!showProgressUpdate && text && (
        <MessageContent>
          {message.role === "assistant" ? (
            <MessageResponse>{text}</MessageResponse>
          ) : (
            <div className="whitespace-pre-wrap">{text}</div>
          )}
        </MessageContent>
      )}
      {message.parts.map((part, index) =>
        part.type === "data-action-link" ? (
          <ActionLinkPart
            key={part.id ?? `${part.data.type}-${part.data.id}-${index}`}
            link={part.data}
            disabled={isAnimating}
          />
        ) : null
      )}
    </Message>
  )
}

// The AI SDK inserts the assistant message as soon as the stream's start
// chunk arrives, before any tokens — rendering that empty bubble adds a
// flex gap that nudges the thinking indicator down. Skip content-free
// messages entirely.
function hasVisibleContent(message: ChatUIMessage): boolean {
  return message.parts.some(
    (part) =>
      (part.type === "text" && part.text.trim().length > 0) ||
      part.type === "data-progress-update" ||
      part.type === "data-tool-call" ||
      part.type === "data-automation-action" ||
      part.type === "data-status" ||
      part.type === "file" ||
      part.type === "data-storage-file" ||
      part.type === "data-action-link"
  )
}

function previousUserMessage(messages: ChatUIMessage[]): ChatUIMessage | undefined {
  return messages.findLast((message) => message.role === "user")
}

function removeInterruptedTurn(messages: ChatUIMessage[]): ChatUIMessage[] {
  const lastUserIndex = messages.findLastIndex((message) => message.role === "user")
  return lastUserIndex === -1 ? messages : messages.slice(0, lastUserIndex)
}

function textFromMessage(message?: ChatUIMessage): string {
  return (message?.parts ?? [])
    .flatMap((part) => (part.type === "text" ? [part.text] : []))
    .join("\n\n")
    .trim()
}

function attachmentsFromParts(parts: ChatUIMessage["parts"]): ChatAttachment[] {
  return parts.flatMap((part, index): ChatAttachment[] => {
    if (part.type === "file") {
      return [
        {
          key: part.url ?? part.filename ?? `file-${index}`,
          url: part.url,
          filename: part.filename,
          mediaType: part.mediaType,
        },
      ]
    }
    if (part.type === "data-storage-file") {
      return [
        {
          key: part.data.file_id ?? part.data.download_url ?? part.data.file_name ?? `storage-${index}`,
          url: part.data.view_url ?? part.data.download_url,
          filename: part.data.file_name,
          mediaType: part.data.file_type,
          size: part.data.file_size,
        },
      ]
    }
    return []
  })
}

async function uploadFileFromPromptPart(filePart: PromptInputMessage["files"][number]): Promise<File> {
  if (filePart.file) {
    return filePart.file
  }

  const response = await fetch(filePart.url ?? "")
  const blob = await response.blob()

  return new File([blob], filePart.filename || "attachment", {
    type: filePart.mediaType || blob.type || "application/octet-stream",
  })
}

function appendFormValue(formData: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) return

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value))
    return
  }

  formData.append(key, String(value))
}

function buildUploadFormData(body: BodyInit | null | undefined, uploadedFiles: File[]) {
  const payload = typeof body === "string" ? (JSON.parse(body) as Record<string, unknown>) : {}
  const formData = new FormData()

  for (const [key, value] of Object.entries(payload)) {
    appendFormValue(formData, key, value)
  }

  for (const file of uploadedFiles) {
    formData.append("uploaded_files[]", file)
  }

  return formData
}
